// =============================================================================
// FORM BUILDER - Main component with SurveyJS integration
// =============================================================================

import { useState, useCallback } from 'react';
import { Eye, EyeOff, Save, Settings, ToggleLeft, ToggleRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { FieldPalette } from './components/FieldPalette';
import { FormCanvas } from './components/FormCanvas';
import { FieldConfigPanel } from './components/FieldConfigPanel';
import { SurveyJSFormBuilder } from './SurveyJSFormBuilder';
import { useFormBuilderStore } from './store';
import type { FieldType, Form } from './types';

type EditorMode = 'custom' | 'surveyjs';

interface FormBuilderProps {
  defaultMode?: EditorMode;
  onSave?: (form: Form) => void;
}

export function FormBuilder({ defaultMode = 'surveyjs', onSave }: FormBuilderProps) {
  const [editorMode, setEditorMode] = useState<EditorMode>(defaultMode);

  const {
    sections,
    selectedFieldId,
    previewMode,
    addField,
    togglePreview,
    exportForm,
    setForm,
  } = useFormBuilderStore();

  const handleDragStart = useCallback(
    (event: React.DragEvent, fieldType: FieldType) => {
      event.dataTransfer.setData('application/formfield', fieldType);
      event.dataTransfer.effectAllowed = 'move';
    },
    []
  );

  const handleAddField = useCallback(
    (fieldType: FieldType) => {
      const sectionId = sections[0]?.id;
      if (sectionId) {
        addField(sectionId, fieldType);
      }
    },
    [sections, addField]
  );

  const handleSave = useCallback(() => {
    const form = exportForm();
    if (onSave) {
      onSave(form);
    }
  }, [exportForm, onSave]);

  const handleSurveyJSSave = useCallback(
    (form: Form) => {
      setForm(form);
      if (onSave) {
        onSave(form);
      }
    },
    [setForm, onSave]
  );

  const toggleEditorMode = useCallback(() => {
    setEditorMode((prev) => (prev === 'custom' ? 'surveyjs' : 'custom'));
  }, []);

  // SurveyJS mode - full screen editor
  if (editorMode === 'surveyjs') {
    return (
      <div className="flex flex-col h-full">
        {/* Toolbar */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-secondary-200 bg-white dark:border-secondary-700 dark:bg-secondary-900">
          <div className="flex items-center gap-2">
            <h2 className="font-semibold text-secondary-900 dark:text-white">
              Form Builder
            </h2>
            <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400">
              SurveyJS
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={toggleEditorMode}
              title="Switch to custom editor"
            >
              <ToggleRight className="h-4 w-4 mr-2" />
              Custom Mode
            </Button>
            <Button variant="outline" size="sm">
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </Button>
          </div>
        </div>

        {/* SurveyJS Creator */}
        <div className="flex-1 overflow-hidden">
          <SurveyJSFormBuilder
            initialForm={exportForm()}
            onSave={handleSurveyJSSave}
          />
        </div>
      </div>
    );
  }

  // Custom mode - original implementation
  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-secondary-200 bg-white dark:border-secondary-700 dark:bg-secondary-900">
        <div className="flex items-center gap-2">
          <h2 className="font-semibold text-secondary-900 dark:text-white">
            Form Builder
          </h2>
          <span className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 dark:bg-secondary-800 dark:text-secondary-400">
            Custom
          </span>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={toggleEditorMode}
            title="Switch to SurveyJS editor"
          >
            <ToggleLeft className="h-4 w-4 mr-2" />
            SurveyJS Mode
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={togglePreview}
          >
            {previewMode ? (
              <>
                <EyeOff className="h-4 w-4 mr-2" />
                Edit
              </>
            ) : (
              <>
                <Eye className="h-4 w-4 mr-2" />
                Preview
              </>
            )}
          </Button>
          <Button variant="outline" size="sm">
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </Button>
          <Button size="sm" onClick={handleSave}>
            <Save className="h-4 w-4 mr-2" />
            Save
          </Button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left sidebar - Field palette */}
        {!previewMode && (
          <div className="w-64 border-r border-secondary-200 bg-white dark:border-secondary-700 dark:bg-secondary-900">
            <FieldPalette
              onDragStart={handleDragStart}
              onAddField={handleAddField}
            />
          </div>
        )}

        {/* Main canvas */}
        <FormCanvas />

        {/* Right sidebar - Field config panel */}
        {!previewMode && selectedFieldId && (
          <div className="w-80 border-l border-secondary-200 bg-white dark:border-secondary-700 dark:bg-secondary-900">
            <FieldConfigPanel />
          </div>
        )}
      </div>
    </div>
  );
}
